/**
 * Het geurpaneel achter "Welke geur past bij mij?".
 *
 * Openen en sluiten doet de browser zelf — de link en de sluitknop gebruiken allebei
 * popovertarget, dus licht-sluiten, Escape en het teruggeven van de focus werken ook
 * zonder dit bestand. Wat hier gebeurt is wat de Popover API niet geeft: het paneel
 * naast de link hangen op desktop, de scroll vastzetten bij de bottom sheet, en de
 * tabs.
 *
 * Een knop mag met `data-swatch-open` zeggen op welk tabblad hij wil openen. Op de PDP
 * is er één ingang; de collectiepagina heeft er twee (naast de kop en in de geurtegel).
 * Het paneel hangt dan aan de knop die het opende, en `data-swatch-source` gaat mee in
 * scent_guide_open.
 */

/** Onder deze breedte is het paneel een bottom sheet; de CSS gebruikt dezelfde grens. */
const SHEET_MAX_WIDTH = 749;

/** Ruimte tussen de link en het paneel. */
const PANEL_GAP = 8;

/** Marge die het paneel van de rand van de viewport houdt. */
const VIEWPORT_MARGIN = 8;

class ScentPanelComponent extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #anchorObserver;

  /** @type {HTMLElement | null} */
  #anchor = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panes = [];

  /** Welke tab de knop wil die zojuist is aangeklikt. */
  #pendingTab = null;

  /** @type {string | null} Waar de knop die het paneel opende staat. */
  #source = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });

    this.#anchor = this.querySelector('[data-swatch-trigger]');
    this.#panel = this.querySelector('[data-swatch-panel]');
    // Een groep zonder geurprofielen heeft geen paneel: dan blijft alleen het
    // tellen van klikken op de pillen over.
    if (!this.#anchor || !this.#panel) return;

    this.#tabs = Array.from(this.#panel.querySelectorAll('[role="tab"]'));
    this.#panes = this.#tabs
      .map((tab) => {
        const id = tab.getAttribute('aria-controls');
        return id ? this.#panel?.querySelector(`#${CSS.escape(id)}`) : null;
      })
      .filter((pane) => pane instanceof HTMLElement);

    this.#panel.addEventListener('beforetoggle', this.#onBeforeToggle, { signal });
    this.#panel.addEventListener('toggle', this.#onToggle, { signal });
    this.#panel.addEventListener('keydown', this.#onKeydown, { signal });

    // capture, omdat de productkolom zelf in een scrollende container kan zitten:
    // een scroll-event daarvan bereikt window niet in de bubbelfase.
    window.addEventListener('scroll', this.#position, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#position, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#anchorObserver?.disconnect();
    this.#unlockScroll();
  }

  get #isSheet() {
    return window.innerWidth <= SHEET_MAX_WIDTH;
  }

  /**
   * Alle klikken binnen de groep: een tabblad, een link naar een andere geur, of de
   * knop die het paneel opent. Die laatste onthoudt welke tab hij wil — dit loopt
   * vóór het openen van de popover, dus in #onToggle staat de wens al klaar.
   *
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement) {
      this.#selectTab(this.#tabs.indexOf(tab), false, true);
      return;
    }

    const link = target.closest('[data-scent-to]');
    if (link instanceof HTMLElement) {
      const inPanel = this.#panel?.contains(link) ?? false;
      this.#publish('scent_guide_click', {
        to: link.dataset.scentTo,
        // waar de klik vandaan kwam: de rij zelf, of een van de tabbladen
        from_ui: inPanel ? this.#currentTabName() : 'pill',
      });
      // Het meetplan telt het wisselen van geur via de knoppen apart.
      if (!inPanel) this.#publish('pdp_swatch_click', { to: link.dataset.scentTo });
      return;
    }

    const trigger = target.closest('[data-swatch-open]');
    if (trigger instanceof HTMLElement && !this.#panel?.contains(trigger)) {
      this.#pendingTab = trigger.dataset.swatchOpen ?? null;
      this.#source = trigger.dataset.swatchSource ?? null;
      if (trigger.hasAttribute('data-swatch-trigger')) this.#anchor = trigger;
    }
  };

  /**
   * De CSS zet het paneel gecentreerd neer, want zonder JS is dat de enige plek die
   * altijd klopt. Daardoor stond het één frame in het midden voordat #position het
   * naast de link hing — zichtbaar als een sprongetje. `beforetoggle` loopt vóór het
   * tonen, dus hier wordt het onzichtbaar gezet; #onToggle meet, plaatst en laat het
   * dan pas zien. Meten kan hier nog niet: het element staat op display: none.
   *
   * Draait de JS niet, dan wordt het attribuut nooit gezet en valt alles terug op de
   * gecentreerde CSS — zichtbaar, alleen niet naast de link.
   *
   * @param {Event} event
   */
  #onBeforeToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState !== 'open') return;
    if (this.#isSheet) return;
    this.#panel?.setAttribute('data-placing', '');
  };

  /** @param {Event} event */
  #onToggle = (event) => {
    const open = /** @type {ToggleEvent} */ (event).newState === 'open';
    this.#anchor?.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!open) {
      this.#panel?.removeAttribute('data-placing');
      this.#anchorObserver?.disconnect();
      this.#anchorObserver = undefined;
      this.#unlockScroll();
      this.#clearPosition();
      this.#pendingTab = null;
      return;
    }

    if (this.#pendingTab) {
      const index = this.#tabs.findIndex((tab) => tab.dataset.swatchTab === this.#pendingTab);
      if (index >= 0) this.#selectTab(index, false, false);
    }

    this.#position();
    this.#panel?.removeAttribute('data-placing');

    if (this.#isSheet) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else if (this.#anchor) {
      // Scrollt de link uit beeld, dan hangt het paneel aan niets meer.
      this.#anchorObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry && !entry.isIntersecting) this.#panel?.hidePopover();
        },
        { threshold: 0 }
      );
      this.#anchorObserver.observe(this.#anchor);
    }

    this.#publish('scent_guide_open', {
      tab: this.#currentTabName(),
      ...(this.#source ? { source: this.#source } : {}),
    });
  };

  /**
   * Rechts uitlijnen op de link, en binnen de viewport blijven. Een popover staat in
   * de top layer, dus position: fixed rekent tegen de viewport — er is geen voorouder
   * die dit kan afknippen. De breedte komt uit de CSS; die matchen met een link van
   * 160px zou het paneel onbruikbaar smal maken.
   */
  #position = () => {
    const panel = this.#panel;
    const anchor = this.#anchor;
    if (!panel || !anchor || !panel.matches(':popover-open')) return;

    if (this.#isSheet) {
      this.#clearPosition();
      return;
    }

    // De inline translate moet weg vóór het meten: met de val-terug van -50% -50%
    // staat het paneel gecentreerd en meet de rect op de verkeerde plek.
    panel.style.translate = 'none';

    const box = anchor.getBoundingClientRect();
    const rect = panel.getBoundingClientRect();

    let left = box.right - rect.width;
    left = Math.min(left, window.innerWidth - rect.width - VIEWPORT_MARGIN);
    left = Math.max(VIEWPORT_MARGIN, left);

    let top = box.bottom + PANEL_GAP;
    if (top + rect.height > window.innerHeight - VIEWPORT_MARGIN) {
      const above = box.top - PANEL_GAP - rect.height;
      top =
        above >= VIEWPORT_MARGIN
          ? above
          : Math.max(VIEWPORT_MARGIN, window.innerHeight - rect.height - VIEWPORT_MARGIN);
    }

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  };

  /** Inline stijlen weghalen, zodat de media query van de bottom sheet weer geldt. */
  #clearPosition() {
    const panel = this.#panel;
    if (!panel) return;
    panel.style.removeProperty('left');
    panel.style.removeProperty('top');
    panel.style.removeProperty('translate');
  }

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[role="tab"]')) return;

    const current = this.#tabs.findIndex((tab) => tab === target.closest('[role="tab"]'));
    if (current < 0) return;

    let next = current;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectTab(next, true, true);
  };

  /**
   * @param {number} index
   * @param {boolean} moveFocus
   * @param {boolean} track - uit bij het openen, anders telt elke opening dubbel
   */
  #selectTab(index, moveFocus, track) {
    const tab = this.#tabs[index];
    if (!tab || tab.getAttribute('aria-selected') === 'true') return;

    this.#tabs.forEach((item, i) => {
      const selected = i === index;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Eén stop in de taborde: de pijltjes doen de rest.
      if (selected) {
        item.removeAttribute('tabindex');
      } else {
        item.setAttribute('tabindex', '-1');
      }
      const pane = this.#panes[i];
      if (pane) pane.hidden = !selected;
    });

    if (moveFocus) tab.focus();

    // De inhoud van het andere tabblad is bijna altijd langer of korter, dus na het
    // wisselen moet het paneel opnieuw gehangen worden.
    this.#position();
    if (track) this.#publish('scent_guide_tab', { tab: this.#currentTabName() });
  }

  #currentTabName() {
    const active = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    return active?.dataset.swatchTab ?? 'options';
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} payload
   */
  #publish(name, payload) {
    try {
      window.Shopify?.analytics?.publish(name, {
        from: this.dataset.scentFrom,
        ...payload,
      });
    } catch (error) {
      // tracking mag de geurkeuze nooit in de weg zitten
    }
  }
}

if (!customElements.get('scent-panel-component')) {
  customElements.define('scent-panel-component', ScentPanelComponent);
}
